import React, { useState, useEffect } from "react";
import CustomerRequestsTable from "./Components/CustomerRequestsTable";
import { SearchIcon, RotateCw, X, Settings, FilterX } from "lucide-react";
import { IconButton } from "@mui/material";
import { errorToast, successToast } from "../../../services/toast.service";
import CustomerRequestFilters from "./Components/CustomerRequestFilters";
import { defaultColumns } from "./Components/CustomerRequestsTable";

const CustomerRequest = () => {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refetchFn, setRefetchFn] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [visibleColumns, setVisibleColumns] = useState(
    defaultColumns.reduce((acc, col) => ({ ...acc, [col.id]: true }), {}),
  );
  const [filters, setFilters] = useState({
    storeType: "",
    status: "",
    sortBy: "",
  });

  // Debounce search query to prevent too many API calls
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  const handleRefresh = async () => {
    if (refetchFn) {
      setIsRefreshing(true);
      try {
        await refetchFn();
        successToast("Customer requests refreshed");
      } catch (error) {
        errorToast("Failed to refresh customer requests");
      } finally {
        setIsRefreshing(false);
      }
    }
  };

  const handleSearchChange = event => {
    setSearchQuery(event.target.value);
  };

  const clearSearch = () => {
    setSearchQuery("");
  };

  const handleSettingsClick = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseSettings = () => {
    setAnchorEl(null);
  };

  const handleColumnToggle = columnId => {
    const column = defaultColumns.find(col => col.id === columnId);
    if (column?.required) return;

    setVisibleColumns(prev => ({
      ...prev,
      [columnId]: !prev[columnId],
    }));
  };

  const handleClearFilters = () => {
    setFilters({
      storeType: "",
      status: "",
      sortBy: "",
    });
    setSearchQuery("");
  };

  return (
    <div className="bg-white h-[calc(100vh-120px)]">
      {/* Header Section */}
      <div className="flex items-center justify-between px-6 py-4 border-b">
        <h1 className="text-2xl font-semibold font-outfit text-gray-800">
          Customer Requests
        </h1>

        {/* Search and Settings Section */}
        <div className="flex items-center gap-4">
          {/* Search Bar */}
          <div className="w-[550px]">
            <div className="relative">
              <SearchIcon
                className="absolute left-3 top-1/2 transform -translate-y-1/2"
                size={18}
                color="#83899F"
              />
              <input
                type="text"
                value={searchQuery}
                onChange={handleSearchChange}
                placeholder="Search by Business, Owner, Phone Number, GSTIN, City.."
                className="w-full h-10 pl-10 pr-10 rounded-lg border border-gray-200 
                         focus:border-[#5652B0] focus:ring-1 focus:ring-[#5652B0] 
                         outline-none transition-all text-gray-600"
              />
              {searchQuery && (
                <button
                  onClick={clearSearch}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 hover:bg-gray-100 p-1 rounded-full">
                  <X size={16} color="#83899F" />
                </button>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2">
            <IconButton
              onClick={handleRefresh}
              className="hover:bg-gray-100"
              disabled={isRefreshing}
              title="Refresh">
              <RotateCw
                size={20}
                className={`text-gray-600 ${
                  isRefreshing ? "animate-spin" : ""
                }`}
              />
            </IconButton>
            <IconButton
              onClick={handleSettingsClick}
              className="hover:bg-gray-100"
              title="Column Settings">
              <Settings size={20} className="text-gray-600" />
            </IconButton>
          </div>
        </div>
      </div>

      <div className="p-6">
        {/* Filters Section with Clear Filters button */}
        <div className="mb-6 flex items-center justify-between">
          <div className="flex-1">
            <CustomerRequestFilters
              filters={filters}
              onFilterChange={setFilters}
            />
          </div>
          {(filters.storeType ||
            filters.status ||
            filters.sortBy ||
            searchQuery) && (
            <button
              onClick={handleClearFilters}
              className="flex items-center gap-2 px-3 py-2 ml-4 text-sm font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors">
              <FilterX size={16} />
              Clear Filters
            </button>
          )}
        </div>

        <CustomerRequestsTable
          searchQuery={debouncedSearchQuery}
          setRefetchFn={setRefetchFn}
          anchorEl={anchorEl}
          onCloseSettings={handleCloseSettings}
          visibleColumns={visibleColumns}
          onColumnToggle={handleColumnToggle}
          filters={filters}
        />
      </div>
    </div>
  );
};

// Add debounce hook
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

export default CustomerRequest;
